import { useEffect, useState } from 'react'
import type { BrandingSurface } from '@/api/endpoints/kiosk-branding'
import { imagesApi } from '@/api/endpoints/images'

/**
 * What the screen will actually look like (CHG-2026-08-kiosk-screen-branding).
 *
 * This exists because ads are rendered with `cover`: the image fills the screen and the overflow is
 * cropped. That is the look everyone wanted, and it is also the one thing that can silently eat the
 * QR code and the store badges an ad exists for. So the crop has to be visible *here*, before
 * publishing, at the real aspect ratio of the screen in question.
 *
 * The two rules below must stay identical to the kiosk's:
 *   - the frame is the configured target width×height ratio,
 *   - the ad is `object-fit: cover` / `object-position: center`.
 * They are duplicated rather than imported because the two apps share no UI package today; the
 * kiosk side lives in `apps/kiosk-web/src/components/branding/ad-rotator.tsx` and any change to one
 * has to be made in the other.
 */
export function SurfacePreview({
  surface,
  fallbackWidth,
  fallbackHeight,
  emptyLabel,
}: {
  surface: BrandingSurface
  fallbackWidth: number
  fallbackHeight: number
  emptyLabel: string
}) {
  const ads = (surface.ads ?? []).filter((ad) => ad.active && ad.media)
  const [index, setIndex] = useState(0)

  // Clamp when the playlist shrinks under us (an ad removed while previewing).
  useEffect(() => {
    if (index >= ads.length) setIndex(0)
  }, [ads.length, index])

  const width = surface.target?.width || fallbackWidth
  const height = surface.target?.height || fallbackHeight
  const current = ads.length > 0 ? ads[Math.min(index, ads.length - 1)] : null
  const logoUrl = imagesApi.mediaPreviewUrl(surface.logo)
  const adUrl = imagesApi.mediaPreviewUrl(current?.media)

  return (
    <div className="space-y-2">
      <div
        className="relative w-full overflow-hidden rounded-lg border"
        style={{
          aspectRatio: `${width} / ${height}`,
          backgroundColor: surface.colors?.background || '#ffffff',
          color: surface.colors?.text || '#000000',
        }}
      >
        {adUrl ? (
          <img
            src={adUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        ) : logoUrl ? (
          // Without ads the logo is the screen — same as the kiosk, which omits the logo once ads
          // are configured rather than pinning it over someone's artwork.
          <div className="absolute inset-0 flex items-center justify-center p-[8%]">
            <img
              src={logoUrl}
              alt=""
              className="max-h-[40%] max-w-[60%] object-contain"
            />
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-sm opacity-60">
            {emptyLabel}
          </div>
        )}
      </div>
      {ads.length > 1 && (
        <div className="flex items-center gap-2 text-sm">
          {ads.map((ad, i) => (
            <button
              key={`${ad.media}-${i}`}
              type="button"
              onClick={() => setIndex(i)}
              className={`h-2 w-6 rounded-full ${i === index ? 'bg-primary' : 'bg-muted'}`}
              aria-label={`${i + 1}`}
            />
          ))}
        </div>
      )}
      <p className="text-muted-foreground text-xs">
        {width} × {height}
      </p>
    </div>
  )
}
